import { useMemo } from "react";
import { 
  showAddOrEditCouncilState, 
  showAddOrUpdateServiceState,
  showAddOrUpdateInternshipSiteState,
  showDeleteCouncilState,
  showAddOrEditApprovalStructureState,
  showDeleteApprovalStructureState,
  showAddOrEditStudyLevelState,
  showDeleteStudyLevelState,
  showAddOrEditFineState,
  showAddOrEditFineRuleState,
  showDeleteFineState,
  showDeleteQuestionState,
  showEditQuestionState,
  showDeleteExamScheduleState,
  showScheduleExamModalState,
  showSubmitAppealState,
  showInitiateRetakeState,
  showQuestionAuditState
} from "@/atoms";
import useAuth from "@/hooks/useAuth";
import { useUserCouncil } from "@/hooks/useUserCouncil";
import { useRecoilValue } from "recoil";
import { baseUrl } from "@/axios.config";
import { DashboardLayout } from "moh-ui";
import { useTranslation } from "react-i18next";
import { BsFillGearFill } from "react-icons/bs";
import { RiLogoutCircleRLine } from "react-icons/ri";
import { SideBarRouteOption, UserRole } from "@/types";
import { getCouncilProfileUrl } from "@/services/councils";
import DeleteFineModal from "../modals/control-panel/DeleteFine";
import EditQuestionModal from "../modals/questions/EditQuestion";
import AddUpdateFine from "../modals/control-panel/AddUpdateFine";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import ScheduleExamModal from "../modals/exam-schedule/ScheduleExam";
import SubmitAppealModal from "../modals/exam-schedule/SubmitAppeal";
import DeleteQuestionModal from "../modals/questions/DeleteQuestion";
import QuestionAuditsModal from "../modals/questions/QuestionAudits";
import DeleteCouncilModal from "../modals/control-panel/DeleteCouncil";
import AddUpdateCouncil from "../modals/control-panel/AddUpdateCouncil";
import AddUpdateService from "../modals/control-panel/AddUpdateService";
import AddUpdateInternshipSite from "../modals/control-panel/AddUpdateInternshipSite";
import InitiateRetakeModal from "../modals/exam-schedule/InitiateRetake";
import AddUpdateFineRule from "../modals/control-panel/AddUpdateFineRule";
import DeleteStudyLevelModal from "../modals/control-panel/DeleteStudyLevel";
import AddUpdateStudyLevel from "../modals/control-panel/AddUpdateStudyLevel";
import DeleteExamScheduleModal from "../modals/exam-schedule/DeleteExamSchedule";
import { getLicencingEndUserRoutes } from "@/utils/routes/licencingEndUserRoutes";
import DeleteApprovalStructureModal from "../modals/control-panel/DeleteApprovalStructure";
import AddUpdateApprovalStructure from "../modals/licencing-admin/AddUpdateApprovalStructure";

export default function MainLayout({
  routes,
}: {
  routes: SideBarRouteOption[];
}) {
  const { user, logout } = useAuth();
  const council = useUserCouncil();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  // Get modal states to conditionally render
  const showAddUpdateCouncil = useRecoilValue(showAddOrEditCouncilState);
  const showAddUpdateService = useRecoilValue(showAddOrUpdateServiceState);
  const showAddUpdateInternshipSite = useRecoilValue(showAddOrUpdateInternshipSiteState);
  const showDeleteCouncil = useRecoilValue(showDeleteCouncilState);
  const showAddUpdateApprovalStructure = useRecoilValue(showAddOrEditApprovalStructureState);
  const showDeleteApprovalStructure = useRecoilValue(showDeleteApprovalStructureState);
  const showAddUpdateStudyLevel = useRecoilValue(showAddOrEditStudyLevelState);
  const showDeleteStudyLevel = useRecoilValue(showDeleteStudyLevelState);
  const showAddUpdateFine = useRecoilValue(showAddOrEditFineState);
  const showAddUpdateFineRule = useRecoilValue(showAddOrEditFineRuleState);
  const showDeleteFine = useRecoilValue(showDeleteFineState);
  const showDeleteQuestion = useRecoilValue(showDeleteQuestionState);
  const showEditQuestion = useRecoilValue(showEditQuestionState);
  const showDeleteExamSchedule = useRecoilValue(showDeleteExamScheduleState);
  const showScheduleExam = useRecoilValue(showScheduleExamModalState);
  const showSubmitAppeal = useRecoilValue(showSubmitAppealState);
  const showInitiateRetake = useRecoilValue(showInitiateRetakeState);
  const showQuestionAudits = useRecoilValue(showQuestionAuditState);

  const isAdmin = useMemo(() => {
    return user?.roles?.find((role) => role.roleName === UserRole.ADMIN);
  }, [user]);

  const profilePictureUrl = `${baseUrl}/users/profile-picture?pictureName=${user?.profilePicture}`;

  // Use translated routes for end-user-account
  const translatedRoutes = useMemo(() => {
    if (location.pathname.startsWith('/end-user-account')) {
      return getLicencingEndUserRoutes(t);
    }
    return routes;
  }, [routes, location.pathname, t]);

  return (
    <>
      {/* Only render modals when they are actually needed */}
      {showAddUpdateCouncil?.show && <AddUpdateCouncil />}
      {showAddUpdateService?.show && <AddUpdateService />}
      {showAddUpdateInternshipSite?.show && <AddUpdateInternshipSite />}
      {showDeleteCouncil?.show && <DeleteCouncilModal />}
      {showAddUpdateApprovalStructure?.show && <AddUpdateApprovalStructure />}
      {showDeleteApprovalStructure?.show && <DeleteApprovalStructureModal />}
      {showAddUpdateStudyLevel?.show && <AddUpdateStudyLevel />}
      {showDeleteStudyLevel?.show && <DeleteStudyLevelModal />}
      {showAddUpdateFine?.show && <AddUpdateFine />}
      {showAddUpdateFineRule?.show && <AddUpdateFineRule />}
      {showDeleteFine?.show && <DeleteFineModal />}
      {showDeleteQuestion?.show && <DeleteQuestionModal />}
      {showEditQuestion?.show && <EditQuestionModal />}
      {showDeleteExamSchedule?.show && <DeleteExamScheduleModal />}
      {showScheduleExam?.show && <ScheduleExamModal />}
      {showSubmitAppeal?.show && <SubmitAppealModal />}
      {showInitiateRetake && <InitiateRetakeModal />}
      {showQuestionAudits?.show && <QuestionAuditsModal />}

      <DashboardLayout
        collapsibleSidebar
        layoutMode="vertical"
        navigationProps={{
          routes: translatedRoutes,
          isItemActive: (path) => {
            return path === location.pathname;
          },
        }}
        topbarProps={{
          user: {
            username: `${user?.firstName + " " + user?.lastName}`,
            email: user?.email ?? "",
            profilePicture: user?.profilePicture
              ? profilePictureUrl
              : undefined,
          },
          logoProps: {
            title: "Republic of Rwanda",
            subtitle: isAdmin
              ? "Rwanda Health Council Connect (RHCC)"
              : council?.councilName ?? "Rwanda Health Council Connect (RHCC)",
            onClick: () => navigate("/" + location.pathname.split("/")[1]),
            customLogo: isAdmin ? undefined : council?.councilProfileDocumentPath ? (
              <img
                src={getCouncilProfileUrl(council.councilProfileDocumentPath)}
                alt="Council Logo"
                className="h-12 w-12 rounded-full object-cover"
              />
            ) : undefined,
          },
          notificationProps: {
            notifications: [],
            markAllAsRead: () => {},
            markNotificationAsRead: () => {},
          },
          profileDropdownItems: [
            {
              label: t("navigation.accountSettings"),
              icon: <BsFillGearFill size={20} />,
              onClick: () =>
                navigate("/" + location.pathname.split("/")[1] + "/profile"),
            },
            {
              label: t("navigation.logout"),
              icon: <RiLogoutCircleRLine size={20} />,
              onClick: () => logout(),
            },
          ],
        }}
      >
        <Outlet />
      </DashboardLayout>
    </>
  );
}
